import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  Avatar,
  Boton,
  Campo,
  Esqueleto,
  Input,
  Select,
  SelectContenido,
  SelectDisparador,
  SelectOpcion,
  SelectValor,
  TituloPagina,
  Vacio,
  useToast,
} from "@iavmx/ui";
import { Interruptor, Mensaje, Segmento } from "../componentes";
import { leer } from "../api";
import { useSesion } from "../sesion";
import type { Preferencias } from "../tipos";
import { aplicarTema } from "../tema";

export function Ajustes() {
  const { yo, api, token, recargarYo, cerrar } = useSesion();
  const qc = useQueryClient();
  const navegar = useNavigate();
  const { avisar } = useToast();
  const q = useQuery({
    queryKey: ["ajustes"],
    queryFn: () => api.api.ajustes.$get().then((r) => leer<{ preferencias: Preferencias }>(r)),
  });
  // Lo editado manda; mientras no se toque nada, se muestran las preferencias guardadas.
  const [editadas, setEditadas] = React.useState<Preferencias | null>(null);
  const prefs = editadas ?? q.data?.preferencias ?? null;
  const [nombre, setNombre] = React.useState(yo?.nombre ?? "");
  const [encargos, setEncargos] = React.useState(yo?.comoRecibirEncargos ?? "");
  const [sucio, setSucio] = React.useState(false);

  const guardar = useMutation({
    mutationFn: async () => {
      if (!prefs || !yo) return;
      if (nombre.trim().length < 2) throw new Error("El nombre necesita al menos dos letras.");
      await api.api.ajustes.$put({ json: prefs }).then((r) => leer(r));
      if (nombre.trim() !== yo.nombre || (encargos.trim() || null) !== yo.comoRecibirEncargos)
        await api.api.perfil
          .$put({ json: { nombre: nombre.trim(), areaId: yo.areaId, jefeId: yo.jefeId, comoRecibirEncargos: encargos.trim() || null } })
          .then((r) => leer(r));
    },
    onSuccess: async () => {
      setSucio(false);
      await recargarYo();
      void qc.invalidateQueries({ queryKey: ["directorio"] });
      avisar({ titulo: "Cambios guardados", tono: "exito" });
    },
    onError: (e) => avisar({ titulo: "No se guardó", descripcion: e.message, tono: "error" }),
  });

  const subirFoto = useMutation({
    mutationFn: async (archivo: File) => {
      const form = new FormData();
      form.append("foto", archivo);
      const t = await token();
      return leer<{ fotoUrl: string | null }>(
        await fetch("/api/perfil/foto", { method: "POST", body: form, headers: t ? { authorization: `Bearer ${t}` } : {} }),
      );
    },
    onSuccess: async () => {
      await recargarYo();
      avisar({ titulo: "Foto guardada", tono: "exito" });
    },
    onError: (e) => avisar({ titulo: "No se pudo guardar la foto", descripcion: e.message, tono: "error" }),
  });

  if (!yo) return null;
  if (q.isLoading || !prefs) return <Esqueleto className="h-40" />;
  if (q.error) return <Vacio titulo="No se pudo cargar" texto={q.error.message} />;
  const cambiar = (parcial: Partial<Preferencias>) => {
    setEditadas({ ...prefs, ...parcial });
    setSucio(true);
    if (parcial.tema) aplicarTema(parcial.tema);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        guardar.mutate();
      }}
      noValidate
    >
      <TituloPagina
        descripcion="Cómo te avisamos y cómo se ve el portal. Aplica a todas tus herramientas."
        accion={
          <Boton type="submit" variante="acento" className="h-11 px-6 text-tinta" disabled={guardar.isPending || !sucio}>
            {guardar.isPending ? "Guardando…" : sucio ? "Guardar cambios" : "Sin cambios"}
          </Boton>
        }
      >
        Ajustes
      </TituloPagina>
      <div className="grid gap-10 lg:grid-cols-3">
        <section className="space-y-4" aria-labelledby="cuenta">
          <h2 id="cuenta" className="font-titulo text-xl font-bold">Tu cuenta</h2>
          <div className="flex items-center gap-4">
            <Avatar nombre={yo.nombre} src={yo.fotoUrl} className="size-16" />
            <div className="min-w-0 font-ui text-sm">
              <p className="font-semibold">{yo.nombre}</p>
              <p className="truncate text-tinta-tenue">{yo.correo}</p>
              <p className="text-tinta-tenue">{[yo.puesto, yo.areaNombre, yo.jefeNombre ? `reporta a ${yo.jefeNombre}` : null].filter(Boolean).join(" · ")}</p>
              <label className="mt-2 inline-flex min-h-9 cursor-pointer items-center border border-linea-fuerte px-3 text-xs font-semibold hover:bg-fondo-suave focus-within:ring-2 focus-within:ring-ring">
                {subirFoto.isPending ? "Subiendo…" : "Cambiar foto"}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" disabled={subirFoto.isPending} onChange={(e) => { const f = e.target.files?.[0]; if (f) subirFoto.mutate(f); e.target.value = ""; }} />
              </label>
            </div>
          </div>
          <Campo etiqueta="Nombre como quieres que te vean" htmlFor="nombre">
            <Input id="nombre" value={nombre} className="h-11 text-base" onChange={(e) => { setNombre(e.target.value); setSucio(true); }} />
          </Campo>
          <Campo etiqueta="Cómo prefieres recibir encargos" htmlFor="encargos" ayuda="Aparece en tu ficha del directorio.">
            <Input id="encargos" value={encargos} maxLength={240} className="h-11 text-base" placeholder="WhatsApp solo para urgencias; lo demás en 360" onChange={(e) => { setEncargos(e.target.value); setSucio(true); }} />
          </Campo>
          <p className="font-ui text-xs text-tinta-tenue">Área y jefe los cambia administración.</p>
          <hr className="border-linea" />
          <Boton type="button" variante="liga" onClick={() => void cerrar().then(() => navegar({ to: "/entrar" }))}>Cerrar sesión en este dispositivo</Boton>
        </section>

        <section aria-labelledby="avisos">
          <h2 id="avisos" className="mb-2 font-titulo text-xl font-bold">Avisos</h2>
          <Interruptor etiqueta="Avisos por correo" descripcion="Lo que pasa en tus herramientas te llega a tu correo de Vanguardia." activo={prefs.avisosCorreo} onCambio={(v) => cambiar({ avisosCorreo: v })} />
          <fieldset className="pl-14" disabled={!prefs.avisosCorreo}>
            <legend className="sr-only">Frecuencia de los avisos por correo</legend>
            {(
              [
                ["momento", "Cada aviso al momento", "Un correo por aviso. Son varios al día."],
                ["urgentes", "Lo urgente al momento, el resto en el resumen", "Vencimientos de hoy y campañas en riesgo llegan solos."],
                ["resumen", "Solo el resumen", "Nada suelto; todo junto una vez al día."],
              ] as const
            ).map(([valor, titulo, desc]) => (
              <label key={valor} className="flex min-h-11 cursor-pointer items-start gap-3 border-b border-linea py-2.5 disabled:opacity-50">
                <input type="radio" name="frecuencia" value={valor} checked={prefs.frecuenciaAvisos === valor} onChange={() => cambiar({ frecuenciaAvisos: valor })} className="mt-1 size-4 accent-tinta" />
                <span><span className="block font-ui text-sm font-semibold">{titulo}</span><span className="block font-ui text-sm text-tinta-tenue">{desc}</span></span>
              </label>
            ))}
          </fieldset>
          <Interruptor etiqueta="Resumen diario" descripcion="Un correo con lo que te toca hoy y lo que venció ayer, de lunes a viernes." activo={prefs.resumenDiario} onCambio={(v) => cambiar({ resumenDiario: v })} />
          {prefs.resumenDiario && (
            <div className="border-b border-linea py-3 pl-14">
              <Campo etiqueta="Hora del resumen" htmlFor="hora">
                <Select value={prefs.horaResumen} onValueChange={(v) => cambiar({ horaResumen: v as Preferencias["horaResumen"] })}>
                  <SelectDisparador id="hora" className="h-11 w-32 text-base"><SelectValor /></SelectDisparador>
                  <SelectContenido>
                    {(["7:00", "8:00", "9:00"] as const).map((h) => <SelectOpcion key={h} value={h}>{h}</SelectOpcion>)}
                  </SelectContenido>
                </Select>
              </Campo>
            </div>
          )}
          <Interruptor etiqueta="Avisos por WhatsApp" descripcion="Disponible cuando iavmx tenga su número verificado. Mientras, correo." activo={false} onCambio={() => undefined} deshabilitado />
        </section>

        <section className="space-y-4" aria-labelledby="apariencia">
          <h2 id="apariencia" className="font-titulo text-xl font-bold">Apariencia</h2>
          <div className="space-y-2">
            <p className="font-ui text-xs font-semibold uppercase tracking-wide text-tinta-3">Modo</p>
            <Segmento etiqueta="Modo de color" valor={prefs.tema} onCambio={(v) => cambiar({ tema: v })} opciones={[{ valor: "claro", nombre: "Claro" }, { valor: "oscuro", nombre: "Oscuro" }, { valor: "sistema", nombre: "Como tu dispositivo" }]} />
            <p className="font-ui text-xs text-tinta-tenue">El modo oscuro se llama Coffee Noir y aplica en todas las herramientas.</p>
          </div>
          <hr className="border-linea" />
          <Interruptor etiqueta="Abrir las herramientas en la misma pestaña" descripcion="Si lo apagas, cada herramienta abre en una pestaña nueva." activo={prefs.abrirEnMismaPestana} onCambio={(v) => cambiar({ abrirEnMismaPestana: v })} />
          {sucio && <Mensaje tono="info">Tienes cambios sin guardar.</Mensaje>}
        </section>
      </div>
    </form>
  );
}
